import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Accordion, AccordionItem, AccordionTitle, AccordionDescription, Typography } from '@pw-tech/omni-ui';
import FileUploadComponent from '@/modules/common/components/FileUploadComponent/FileUploadComponent';
import FooterPortal from '@/modules/common/Portals/FooterPortal/FooterPortal';
import { getNextStep } from '@/modules/common/utils';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { useRouter } from 'next/router';
const UploadDocuments = () => {
  const {
    registrationDetails,
    submitRegisterForm,
    isFormSubmitting,
    setRegistrationDetails
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  const isSkipCee = selectedCourseData?.skipCeeExam;
  const isTwelfthMarksheetRequired = selectedCourseData?.skipCeeExam || selectedCourseData?.batchCrmName?.includes('SOT');
  const hasWorkExp = registrationDetails?.hasWorkExperience;
  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  const [tenthResult, setTenthResult] = useState<string>(registrationDetails?.tenthResult || '');
  const [twelfthResult, setTwelfthResult] = useState<string>(registrationDetails?.twelfthResult || '');
  const [competitiveExamDocument, setCompetitiveExamDocument] = useState<string>(registrationDetails?.competitiveExamDocument || '');
  const [ewsCertificate, setEwsCertificate] = useState<string>(registrationDetails?.ewsCertificate || '');
  const [ugMarksheet, setUgMarksheet] = useState<string>(registrationDetails?.ugMarksheet || '');
  const [degreeCertificate, setDegreeCertificate] = useState<string>(registrationDetails?.degreeCertificate || '');
  const [workExperienceDocument, setWorkExperienceDocument] = useState<string>(registrationDetails?.workExperienceDocument || '');
  const [cvResume, setCvResume] = useState<string>(registrationDetails?.cvResume || '');
  const router = useRouter();
  const [expandedRemarks, setExpandedRemarks] = useState<string | null>(null);
  const [isRemarksLocked, setIsRemarksLocked] = useState<boolean>(false);
  const toggleRemarks = (key: string) => {
    if (expandedRemarks === key && isRemarksLocked) {
      setExpandedRemarks(null);
      setIsRemarksLocked(false);
    } else {
      setExpandedRemarks(key);
      setIsRemarksLocked(true);
    }
  };
  const handleRemarksHoverEnter = (key: string) => {
    if (!isRemarksLocked) {
      setExpandedRemarks(key);
    }
  };
  const handleRemarksHoverLeave = () => {
    if (!isRemarksLocked) {
      setExpandedRemarks(null);
    }
  };
  const handleBackdropClick = () => {
    setExpandedRemarks(null);
    setIsRemarksLocked(false);
  };
  const isDocumentReUploaded = useCallback((documentKey: string) => {
    const currentUploads: any = {
      tenthResult,
      twelfthResult,
      competitiveExamDocument,
      ewsCertificate,
      ugMarksheet,
      degreeCertificate,
      workExperienceDocument,
      cvResume
    };
    const originalUploads: any = {
      tenthResult: registrationDetails?.tenthResult || '',
      twelfthResult: registrationDetails?.twelfthResult || '',
      competitiveExamDocument: registrationDetails?.competitiveExamDocument || '',
      ewsCertificate: registrationDetails?.ewsCertificate || '',
      ugMarksheet: registrationDetails?.ugMarksheet || '',
      degreeCertificate: registrationDetails?.degreeCertificate || '',
      workExperienceDocument: registrationDetails?.workExperienceDocument || '',
      cvResume: registrationDetails?.cvResume || ''
    };
    const current = currentUploads[documentKey]?.trim() || '';
    const original = originalUploads[documentKey]?.trim() || '';
    return current !== '' && current !== original;
  }, [tenthResult, twelfthResult, competitiveExamDocument, ewsCertificate, ugMarksheet, degreeCertificate, workExperienceDocument, cvResume, registrationDetails]);
  const getApprovalStatusChip = (approvalData: {
    status: string;
    remarks: string;
  } | undefined, documentKey: string) => {
    // Don't show approval status for work experience documents and CV/Resume
    if (documentKey === 'workExperienceDocument' || documentKey === 'cvResume') {
      return null;
    }
    const uploads: Record<string, string> = {
      tenthResult,
      twelfthResult,
      competitiveExamDocument,
      ewsCertificate,
      ugMarksheet,
      degreeCertificate,
      workExperienceDocument,
      cvResume
    };
    const isUploaded = Boolean(uploads[documentKey]?.trim());
    if (!isUploaded) return null;
    let effectiveApprovalData = approvalData;
    if (!effectiveApprovalData?.status) {
      // Default to Pending for uploaded documents without approval status
      effectiveApprovalData = {
        status: 'Pending',
        remarks: ''
      };
    }

    // If the document has been re-uploaded with a new file, reset to Pending
    if (isDocumentReUploaded(documentKey)) {
      effectiveApprovalData = {
        status: 'Pending',
        remarks: ''
      };
    }
    const statusConfig: Record<string, {
      text: string;
      chipClass: string;
      iconColor: string;
      bgColor: string;
    }> = {
      Approved: {
        text: 'Approved',
        chipClass: 'bg-[#DFF1E4] text-[#1B7938]',
        iconColor: '#1B7938',
        bgColor: '#DFF1E4'
      },
      Pending: {
        text: 'Approval Pending',
        chipClass: 'bg-[#FEFBE8] text-[#A15C07]',
        iconColor: '#A15C07',
        bgColor: '#FEFBE8'
      },
      Rejected: {
        text: 'Rejected',
        chipClass: 'bg-[#FEE2E2] text-[#DC2626]',
        iconColor: '#DC2626',
        bgColor: '#FEE2E2'
      }
    };
    const config = statusConfig[effectiveApprovalData.status] || statusConfig.Pending;
    const showInfoButton = effectiveApprovalData.status === 'Rejected';
    const isExpanded = expandedRemarks === documentKey;
    return <div className="flex items-center gap-1.5 relative" data-sentry-component="getApprovalStatusChip" data-sentry-source-file="UploadDocuments.tsx">
        <span className={`px-2 py-0.5 text-[10px] font-semibold leading-4 rounded ${config.chipClass}`}>
          {config.text}
        </span>
        {showInfoButton && effectiveApprovalData.remarks && <div onMouseEnter={() => handleRemarksHoverEnter(documentKey)} onMouseLeave={handleRemarksHoverLeave}>
            <button type="button" onClick={() => toggleRemarks(documentKey)} className="flex items-center justify-center hover:opacity-70 transition-opacity p-1" aria-label="View remarks">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7" stroke={config.iconColor} strokeWidth="1.5" fill="none" />
                <path d="M8 7V11" stroke={config.iconColor} strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="8" cy="5" r="0.75" fill={config.iconColor} />
              </svg>
            </button>
            {isExpanded && <>
                {/* Backdrop - only shown when locked via click */}
                {isRemarksLocked && <div className="fixed inset-0 z-40" onClick={handleBackdropClick} />}
                {/* Popover */}
                <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] w-max max-w-full max-h-[200px] overflow-y-auto p-3 rounded-lg shadow-lg border" style={{
            backgroundColor: config.bgColor,
            borderColor: config.iconColor
          }}>
                  <div className="flex items-start gap-2">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-0.5 flex-shrink-0">
                      <path d="M8 1C4.13 1 1 4.13 1 8C1 11.87 4.13 15 8 15C11.87 15 15 11.87 15 8C15 4.13 11.87 1 8 1ZM8.5 11.5H7.5V7H8.5V11.5ZM8.5 5.5H7.5V4.5H8.5V5.5Z" fill={config.iconColor} />
                    </svg>
                    <Typography className="!text-xs font-medium" style={{
                color: config.iconColor,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word'
              }}>
                      {effectiveApprovalData.remarks}
                    </Typography>
                  </div>
                </div>
              </>}
          </div>}
      </div>;
  };
  const getUploadStatus = (fileKeys: string[]) => {
    const uploads: any = {
      tenthResult,
      twelfthResult,
      competitiveExamDocument,
      ewsCertificate,
      ugMarksheet,
      degreeCertificate,
      workExperienceDocument,
      cvResume
    };
    // Fix: Check if the value exists and is not empty string
    const uploaded = fileKeys.filter(key => uploads[key] && uploads[key].trim() !== '').length;
    if (uploaded === fileKeys?.length) {
      return {
        text: `${uploaded}/${fileKeys.length} UPLOADED`,
        className: 'bg-[#DFF1E4] text-[#1B7938]'
      };
    }
    if (uploaded) {
      return {
        text: `${uploaded}/${fileKeys.length} UPLOADED`,
        className: 'bg-[#FEFBE8] text-[#A15C07]'
      };
    }
    return {
      text: 'PENDING',
      className: 'bg-[#FEFBE8] text-[#A15C07]'
    };
  };
  const getOverallApprovalStatus = (fileKeys: string[]) => {
    const uploads: Record<string, string> = {
      tenthResult,
      twelfthResult,
      competitiveExamDocument,
      ewsCertificate,
      ugMarksheet,
      degreeCertificate,
      workExperienceDocument,
      cvResume
    };

    // If no documents have been uploaded at all, don't show any approval status
    const uploadedKeys = fileKeys.filter(key => uploads[key] && uploads[key].trim() !== '');
    if (uploadedKeys.length === 0) return null;

    // Treat missing (not-uploaded) documents as Pending so we never
    // surface an aggregate "Approved" while the section is partially uploaded
    const statuses = fileKeys.map(key => {
      const isUploaded = uploads[key] && uploads[key].trim() !== '';
      if (!isUploaded) return 'Pending';
      if (isDocumentReUploaded(key)) return 'Pending';
      const approvalData = (registrationDetails?.documentApprovalStatus as any)?.[key];
      if (!approvalData?.status) return 'Pending';
      return approvalData.status;
    });
    if (statuses.some(s => s === 'Rejected')) {
      return {
        text: 'Rejected',
        className: 'bg-[#FEE2E2] text-[#DC2626]'
      };
    }
    if (statuses.every(s => s === 'Approved')) {
      return {
        text: 'Approved',
        className: 'bg-[#DFF1E4] text-[#1B7938]'
      };
    }
    return {
      text: 'Approval Pending',
      className: 'bg-[#FEFBE8] text-[#A15C07]'
    };
  };
  const handleUploadComplete = (key: string, data: any) => {
    if (key) {
      const value = data?.data?.data?.fullUrl;
      switch (key) {
        case 'tenthResult':
          setTenthResult(value);
          break;
        case 'twelfthResult':
          setTwelfthResult(value);
          break;
        case 'ewsCertificate':
          setEwsCertificate(value);
          break;
        case 'competitiveExamDocument':
          setCompetitiveExamDocument(value);
          break;
        case 'ugMarksheet':
          setUgMarksheet(value);
          break;
        case 'degreeCertificate':
          setDegreeCertificate(value);
          break;
        case 'workExperienceDocument':
          setWorkExperienceDocument(value);
          break;
        case 'cvResume':
          setCvResume(value);
          break;
        default:
          break;
      }
    }
  };
  const handleToggleItem = (index: number, isExpanded: boolean) => {
    setExpandedIndex(isExpanded ? index : -1);
  };
  const handleRemove = (key: string) => {
    if (key) {
      switch (key) {
        case 'tenthResult':
          setTenthResult('');
          break;
        case 'twelfthResult':
          setTwelfthResult('');
          break;
        case 'competitiveExamDocument':
          setCompetitiveExamDocument('');
          break;
        case 'ewsCertificate':
          setEwsCertificate('');
          break;
        case 'ugMarksheet':
          setUgMarksheet('');
          break;
        case 'degreeCertificate':
          setDegreeCertificate('');
          break;
        case 'workExperienceDocument':
          setWorkExperienceDocument('');
          break;
        case 'cvResume':
          setCvResume('');
          break;
        default:
          break;
      }
    }
  };

  // Calculate if all required files are uploaded
  const isAllFilesUploaded = useMemo(() => {
    // Need to enable later after 12th result
    // const requiredFiles = ['tenthResult', 'twelfthResult'];
    const requiredFiles = ['tenthResult'];

    // Add competitive exam document if eligible exams exist
    if (registrationDetails?.eligibleExams?.length) {
      requiredFiles.push('competitiveExamDocument');
    }

    // Add mandatory docs for skipCeeExam courses
    if (isTwelfthMarksheetRequired) {
      requiredFiles.push('twelfthResult');
    }
    if (isSkipCee) {
      requiredFiles.push('ugMarksheet');
    }

    // Add salary slip & work experience letter if hasWorkExperience
    if (hasWorkExp) {
      requiredFiles.push('workExperienceDocument');
    }

    // Add EWS certificate if user is EWS
    // if (registrationDetails?.isEws) {
    //   requiredFiles.push('ewsCertificate');
    // }

    const uploads: any = {
      tenthResult,
      twelfthResult,
      competitiveExamDocument,
      ewsCertificate,
      ugMarksheet,
      degreeCertificate,
      workExperienceDocument,
      cvResume
    };

    // Check if all required files are uploaded
    return requiredFiles.every(key => uploads[key] && uploads[key].trim() !== '');
  }, [tenthResult, twelfthResult, competitiveExamDocument, ewsCertificate, ugMarksheet, degreeCertificate, workExperienceDocument, cvResume, isSkipCee, isTwelfthMarksheetRequired, hasWorkExp, registrationDetails?.eligibleExams?.length]);
  const handleSubmit = async () => {
    // Track which documents were re-uploaded before submitting
    const docKeys = ['tenthResult', 'twelfthResult', 'competitiveExamDocument', 'ewsCertificate', 'ugMarksheet', 'degreeCertificate', 'workExperienceDocument', 'cvResume'];
    const reUploadedDocs = docKeys.filter(key => isDocumentReUploaded(key));
    const response: any = await submitRegisterForm({
      step: getNextStep(router.query.step, registrationDetails?.step),
      tenthResult,
      twelfthResult,
      competitiveExamDocument,
      ewsCertificate,
      ugMarksheet,
      degreeCertificate,
      workExperienceDocument,
      cvResume
    }, true);

    // After submit, reset approval statuses for re-uploaded docs in context
    // so the Pending status persists when navigating back
    if (reUploadedDocs.length > 0 && response?.data) {
      const updatedApprovalStatus = {
        ...(response.data.documentApprovalStatus || {})
      };
      reUploadedDocs.forEach(key => {
        updatedApprovalStatus[key] = {
          status: 'Pending',
          remarks: ''
        };
      });
      setRegistrationDetails({
        ...response.data,
        documentApprovalStatus: updatedApprovalStatus
      });
    }
  };
  return <div className="flex flex-col gap-6 px-2" data-sentry-component="UploadDocuments" data-sentry-source-file="UploadDocuments.tsx">
      <Typography variant="heading3" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="UploadDocuments.tsx">
        Upload Your Documents
      </Typography>
      <Accordion expandedIndex={expandedIndex} onToggleItem={handleToggleItem} fullWidth data-sentry-element="Accordion" data-sentry-source-file="UploadDocuments.tsx">
        {/* Academic Documents */}
        {(() => {
        const academicKeys = ['tenthResult'];
        if (isTwelfthMarksheetRequired) {
          academicKeys.push('twelfthResult');
        }
        if (registrationDetails?.eligibleExams?.length) {
          academicKeys.push('competitiveExamDocument');
        }
        if (isSkipCee) {
          academicKeys.push('ugMarksheet', 'degreeCertificate');
        }
        return <AccordionItem className="shadow-[0_0_30px_0_rgb(0,0,0,0.12)] h-full">
              <AccordionTitle className="!bg-white">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <Typography variant="heading4" weight="semi-bold" className="!text-sm md:!text-base">
                      Academic Documents
                    </Typography>
                    <span className={`px-2 py-1 !text-xs font-semibold leading-[18px] rounded ${getUploadStatus(academicKeys).className}`}>
                      {getUploadStatus(academicKeys).text}
                    </span>
                    {(() => {
                  const approvalStatus = getOverallApprovalStatus(academicKeys);
                  return approvalStatus ? <span className={`px-2 py-1 !text-xs font-semibold leading-[18px] rounded ${approvalStatus.className}`}>
                          {approvalStatus.text}
                        </span> : null;
                })()}
                  </div>
                </div>
              </AccordionTitle>
              <AccordionDescription className="!bg-white">
                <div className="flex flex-col w-full gap-2 lg:!flex-row">
                  <div className="flex flex-col w-full gap-1">
                    <div className="flex gap-1">
                      <Typography weight="medium" className="!text-sm">
                        Class 10th Marksheet*
                      </Typography>
                      {getApprovalStatusChip(registrationDetails?.documentApprovalStatus?.tenthResult, 'tenthResult')}
                    </div>
                    <FileUploadComponent description="Upload files in .pdf, .png or .jpg format up to 2MB" maxSize={2 * 1024} onUploadComplete={data => handleUploadComplete('tenthResult', data)} onRemove={() => handleRemove('tenthResult')} defaultValue={tenthResult} />
                  </div>
                  <div className="flex flex-col w-full gap-1">
                    <div className="flex gap-1">
                      <Typography weight="medium" className="!text-sm">
                        Class 12th Marksheet{isTwelfthMarksheetRequired ? '*' : ''}
                      </Typography>
                      {getApprovalStatusChip(registrationDetails?.documentApprovalStatus?.twelfthResult, 'twelfthResult')}
                    </div>
                    <FileUploadComponent description="Upload files in .pdf, .png or .jpg format up to 2MB" maxSize={2 * 1024} onUploadComplete={data => handleUploadComplete('twelfthResult', data)} onRemove={() => handleRemove('twelfthResult')} defaultValue={twelfthResult} />
                  </div>
                </div>
                {registrationDetails?.eligibleExams?.length ? <div className="flex flex-col w-full lg:!w-1/2 gap-1 lg:!flex-row sm:!mt-2">
                    <div className="flex flex-col w-full gap-1">
                      <div className="flex flex-col gap-1">
                        <Typography weight="medium" className="!text-sm">
                          {registrationDetails?.eligibleExams ? registrationDetails.eligibleExams.join('/ ') : ''}{' '}
                          Result*
                        </Typography>
                        {getApprovalStatusChip(registrationDetails?.documentApprovalStatus?.competitiveExamDocument, 'competitiveExamDocument')}
                      </div>
                      <FileUploadComponent description="Upload files in .pdf, .png or .jpg format up to 2MB" maxSize={2 * 1024} onUploadComplete={data => handleUploadComplete('competitiveExamDocument', data)} onRemove={() => handleRemove('competitiveExamDocument')} defaultValue={competitiveExamDocument} />
                    </div>
                  </div> : <></>}
                {isSkipCee ? <div className="flex flex-col w-full gap-2 lg:!flex-row sm:!mt-2">
                    <div className="flex flex-col w-full gap-1">
                      <div className="flex flex-col gap-1">
                        <Typography weight="medium" className="!text-sm">
                          UG Marksheet*
                        </Typography>
                        <Typography variant="regular" className="!text-xs !text-[#6B7280]">
                          Combine all your marksheet in a single file
                        </Typography>
                        {getApprovalStatusChip(registrationDetails?.documentApprovalStatus?.ugMarksheet, 'ugMarksheet')}
                      </div>
                      <FileUploadComponent description="Upload files in .pdf, .png or .jpg format up to 2MB" maxSize={2 * 1024} onUploadComplete={data => handleUploadComplete('ugMarksheet', data)} onRemove={() => handleRemove('ugMarksheet')} defaultValue={ugMarksheet} />
                    </div>
                    <div className="flex flex-col w-full gap-1">
                      <div className="flex flex-col gap-1">
                        <Typography weight="medium" className="!text-sm">
                          Degree Certificate
                        </Typography>
                        {getApprovalStatusChip(registrationDetails?.documentApprovalStatus?.degreeCertificate, 'degreeCertificate')}
                      </div>
                      <FileUploadComponent description="Upload files in .pdf, .png or .jpg format up to 2MB" maxSize={2 * 1024} onUploadComplete={data => handleUploadComplete('degreeCertificate', data)} onRemove={() => handleRemove('degreeCertificate')} defaultValue={degreeCertificate} />
                    </div>
                  </div> : <></>}
              </AccordionDescription>
            </AccordionItem>;
      })()}

        {/* Personal Documents */}
        {registrationDetails?.isEws ? <AccordionItem className="shadow-[0_0_30px_0_rgb(0,0,0,0.12)] h-full">
            <AccordionTitle className="!bg-white">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Typography variant="heading4" weight="semi-bold" className="!text-sm md:!text-base">
                    Personal Documents
                  </Typography>
                  <span className={`px-2 py-1 !text-xs font-semibold leading-[18px] rounded ${getUploadStatus(['ewsCertificate']).className}`}>
                    {getUploadStatus(['ewsCertificate']).text}
                  </span>
                  {(() => {
                const approvalStatus = getOverallApprovalStatus(['ewsCertificate']);
                return approvalStatus ? <span className={`px-2 py-1 !text-xs font-semibold leading-[18px] rounded ${approvalStatus.className}`}>
                        {approvalStatus.text}
                      </span> : null;
              })()}
                </div>
              </div>
            </AccordionTitle>
            <AccordionDescription className="!bg-white">
              <div className="flex flex-col w-full lg:!w-1/2 gap-2 lg:!flex-row">
                <div className="flex flex-col w-full gap-1">
                  <div className="flex gap-1">
                    <Typography weight="medium" className="!text-sm">
                      EWS Certificate
                    </Typography>
                    {getApprovalStatusChip(registrationDetails?.documentApprovalStatus?.ewsCertificate, 'ewsCertificate')}
                  </div>
                  <FileUploadComponent description="Upload files in .pdf, .png or .jpg format up to 2MB" maxSize={2 * 1024} onUploadComplete={files => handleUploadComplete('ewsCertificate', files)} onRemove={() => handleRemove('ewsCertificate')} defaultValue={ewsCertificate} />
                </div>
              </div>
            </AccordionDescription>
          </AccordionItem> : <></>}

        {/* Work Experience Documents */}
        {hasWorkExp ? <AccordionItem className="shadow-[0_0_30px_0_rgb(0,0,0,0.12)] h-full">
            <AccordionTitle className="!bg-white">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Typography variant="heading4" weight="semi-bold" className="!text-sm md:!text-base">
                    Work Experience Documents
                  </Typography>
                  <span className={`px-2 py-1 !text-xs font-semibold leading-[18px] rounded ${getUploadStatus(['workExperienceDocument']).className}`}>
                    {getUploadStatus(['workExperienceDocument']).text}
                  </span>
                </div>
              </div>
            </AccordionTitle>
            <AccordionDescription className="!bg-white">
              <div className="flex flex-col w-full lg:!w-1/2 gap-2 lg:!flex-row">
                <div className="flex flex-col w-full gap-1">
                  <div className="flex flex-col gap-1">
                    <Typography weight="medium" className="!text-sm">
                      Last 3 Month Salary Slip & Work Experience Letter (Combined)*
                    </Typography>
                    {getApprovalStatusChip(registrationDetails?.documentApprovalStatus?.workExperienceDocument, 'workExperienceDocument')}
                  </div>
                  <FileUploadComponent description="Upload files in .pdf, .png or .jpg format up to 10MB" maxSize={10 * 1024} onUploadComplete={data => handleUploadComplete('workExperienceDocument', data)} onRemove={() => handleRemove('workExperienceDocument')} defaultValue={workExperienceDocument} />
                </div>
              </div>
            </AccordionDescription>
          </AccordionItem> : <></>}

        {/* CV/Resume - for skipCeeExam courses */}
        {isSkipCee ? <AccordionItem className="shadow-[0_0_30px_0_rgb(0,0,0,0.12)] h-full">
            <AccordionTitle className="!bg-white">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Typography variant="heading4" weight="semi-bold" className="!text-sm md:!text-base">
                    Competition Scorecard
                  </Typography>
                  <span className={`px-2 py-1 !text-xs font-semibold leading-[18px] rounded ${getUploadStatus(['cvResume']).className}`}>
                    {getUploadStatus(['cvResume']).text}
                  </span>
                </div>
              </div>
            </AccordionTitle>
            <AccordionDescription className="!bg-white">
              <div className="flex flex-col w-full lg:!w-1/2 gap-2 lg:!flex-row">
                <div className="flex flex-col w-full gap-1">
                  <div className="flex flex-col gap-1">
                    <Typography weight="medium" className="!text-sm">
                      Competition Scorecard
                    </Typography>
                    {getApprovalStatusChip(registrationDetails?.documentApprovalStatus?.cvResume, 'cvResume')}
                  </div>
                  <FileUploadComponent description="Upload files in .pdf, .png or .jpg format up to 2MB" maxSize={2 * 1024} onUploadComplete={data => handleUploadComplete('cvResume', data)} onRemove={() => handleRemove('cvResume')} defaultValue={cvResume} />
                </div>
              </div>
            </AccordionDescription>
          </AccordionItem> : <></>}
      </Accordion>
      <div className="h-8" />
      <div className="py-2">
        <FooterPortal label="Continue" type="button" btnWrapperClass="relative sm:left-[30%]" disabled={!isAllFilesUploaded} onClick={handleSubmit} isFormSubmitting={isFormSubmitting} data-sentry-element="FooterPortal" data-sentry-source-file="UploadDocuments.tsx" />
      </div>
    </div>;
};
export default UploadDocuments;